import { Component, Inject, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SitesSelectorComponent } from 'src/app/shared/sites-selector/sites-selector.component';
import { AppService } from '../../app.service';
import { HazardControlModel } from '../../models/hazard-control.model';
import { HazardModel } from '../../models/hazard.model';
import { ApiService } from '../../shared/api.service';
import { UtilsService } from '../../shared/utils.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { RISK_LEVEL_OF_CONTROLS } from 'src/app/shared/risk-level-of-controls';
import {
  NetworkedUsersSelectorComponent
} from '../../shared/networked-users-selector/networked-users-selector.component';
import moment from 'moment';
import { CurrentTimezoneStateService } from '../../shared/current-timezone-state.service';
import { HazardsRisksEditComponent } from '../hazards-risks-edit/hazards-risks-edit.component';
import { RiskLevel } from '../../shared/risk-matrix/risk-matrix.component';
import {
  HazardsHierarchyOfControlsComponent
} from '../hazards-hierarchy-of-controls/hazards-hierarchy-of-controls.component';
import { ApiRequestService } from '../../shared/api-request.service';

@Component({
  selector: 'app-hazards-edit',
  templateUrl: './hazards-edit.component.html',
  styleUrls: ['./hazards-edit.component.scss']
})
export class HazardsEditComponent implements OnInit {

  /**
   * Is the form being submitted?
   */
  inProgress = false;

  loc_list: string[] = RISK_LEVEL_OF_CONTROLS;

  search = '';

  selectedFiles: File[] = [];

  reviewedAtDateTime: moment.Moment | null;

  selectedTimezone: string = this.cTmzState.getCurrentTimezone();

  constructor(
    public app: AppService,
    private oldApi: ApiService,
    public router: Router,
    public route: ActivatedRoute,
    public utils: UtilsService,
    private cTmzState: CurrentTimezoneStateService,
    @Inject(MAT_DIALOG_DATA) public hazard: HazardModel,
    private dialogRef: MatDialogRef<HazardsEditComponent>,
    private api: ApiRequestService
  ) {}

  ngOnInit() {
    if ( !this.hazard.id ) {
      this.hazard.selected_site_ids = [];
      this.convertControls();
    } else {
      this.find().then((): void => {
        // Convert unix time into moment object
        if ( this.hazard.reviewer_date ) {
          this.reviewedAtDateTime = moment.unix(this.hazard.reviewer_date);
        }
        this.convertControls();
      });
    }
  }

  /**
   * Converts the hazard controls from a stringified JSON format to an object if they are stored as a string.
   * Ensures that the `hazard.controls` property is parsed into a usable object format.
   *
   * @return {void} Does not return a value.
   */
  convertControls(): void {
    if (typeof this.hazard.controls == 'string') {
      this.hazard.controls = JSON.parse(this.hazard.controls);
    }
  }

  /**
   * Fetches and updates the hazard data for the current hazard ID by making a GET request to the corresponding endpoint.
   *
   * @return {Promise<Object>} A promise that resolves to the updated hazard data retrieved from the API.
   */
  find(): Promise<object> {
    return this.oldApi
      .laravelApiObservable('get', 'hazards/' + this.hazard.id)
      .toPromise().then((response) => (this.hazard = response.data));
  }

  /**
   * Handles the selection of files by adding them to the selectedFiles array.
   *
   * @return {void} - This method does not return a value.
   * @param fileList
   */
  onSelectFiles(fileList: FileList): void {
    const files: File[] = Array.from(fileList);
    if ( files && files.length > 0 ) {
      this.selectedFiles.push(...files);
    }
  }

  /**
   * Uploads the selected files to the server for the specified resource ID.
   * Provides a callback function to be executed after the upload process completes.
   *
   * @param {number} id - The unique identifier of the target resource where files will be uploaded.
   * @param {function} [callback] - Optional callback function executed after the upload process is complete.
   * @return {void} This method does not return a value.
   */
  private uploadSelectedFiles(id: number, callback?: any): void {
    if ( id && this.selectedFiles.length > 0 ) {
      this.api.makeUploadRequest(`v2/file-manager/hazard/${id}`, this.selectedFiles)
        .then((response: any): void => {
          this.utils.showToast('Your files successfully uploaded.');
        })
        .finally((): void => {
          this.selectedFiles.length = 0;
          if ( typeof callback === 'function' ) {
            callback();
          }
        });
    }
  }

  onSelectSites() {
    this.utils.showComponentDialog(
      SitesSelectorComponent,
      {
        selected: this.hazard.selected_site_ids,
        multiple: true
      },
      {
        width: '1200px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.hazard.selected_site_ids = results;
        }
      }
    );
  }

  /**
   * Handles the form submission process. Validates the form and triggers either update or create operations based on the presence of an ID.
   *
   * @param {NgForm} form - The form instance containing user input which needs to be validated and processed.
   * @return {Promise<void>} A promise that resolves when the submission process completes or exits early due to validation failure or in-progress state.
   */
  async onSubmit(form: NgForm) {
    if (this.inProgress) {
      return;
    }

    if (!form.valid || !this.hasValidControls()) {
      this.utils.showModal('Error', 'Please make sure you enter all fields with valid information.');
      return;
    }

    this.inProgress = true;

    if ( this.hazard.id ) {
      // Get unix date/time from moment
      if ( this.reviewedAtDateTime && this.reviewedAtDateTime instanceof moment ) {
        const dateToSave = moment.tz(this.reviewedAtDateTime.format('M/D/YYYY, h:mm:ss a'), 'M/D/YYYY, h:mm:ss a', this.selectedTimezone);
        this.hazard.reviewer_date = dateToSave.unix();
      } else {
        this.hazard.reviewer_date = null;
      }

      return this.update();
    }
    this.create();
  }

  /**
   * Creates a new hazard/risk by sending a POST request to the 'hazards' API endpoint.
   * On success, displays a toast notification and uploads selected files if the response contains a valid ID.
   * Also closes the associated dialog and updates the process status.
   * On failure, displays an error modal and updates the process status.
   *
   * @return {void} This method does not return any value.
   */
  create(): void {
    this.oldApi.laravelApiRequest(
      'post',
      'hazards',
      this.hazard,
      {},
      (response: any): void => {
        this.utils.showToast('The Hazard/Risk was created.');
        this.uploadSelectedFiles(response?.data?.id);
        this.dialogRef.close(true);
        this.inProgress = false;
      },
      (error: HttpErrorResponse): void => {
        this.utils.showModal('Error', error.message);
        this.inProgress = false;
      }
    );
  }

  /**
   * Updates the hazard/risk data by sending a PUT request to the server.
   * Displays a toast notification upon successful update and handles the associated site count message if applicable.
   * Uploads any selected files and triggers a refresh operation.
   * Handles errors by displaying an error modal.
   *
   * @return {void} No return value.
   */
  update(): void {
    this.oldApi.laravelApiRequest(
      'put',
      'hazards/' + this.hazard.id,
      this.hazard,
      {},
      (response: any): void => {
        const site_count_msg: string = (response.meta.associated_site_count && response.meta.associated_site_count > 0 && ((this.hazard.selected_site_ids && this.hazard.selected_site_ids.length > 0) || this.hazard.copy_to_all_sites)) ?
          ` and Copied to ${response.meta.associated_site_count} ${this.utils.getLangTerm('parent-child-sites-combined.plural', 'Sites').toLowerCase()}` :
          '';
        this.utils.showToast(`The Hazard/Risk was updated${site_count_msg}.`);
        this.inProgress = false;
        this.find();
      },
      (error: HttpErrorResponse): void => {
        this.utils.showModal('Error', error.message);
        this.inProgress = false;
      }
    );
  }

  /**
   * Validate hazard controls.
   */
  hasValidControls() {
    if (this.hazard.controls.length > 0) {
      let hasValidControls = true;
      this.hazard.controls.forEach((control) => {
        if (!control.control || !control.loc) {
          hasValidControls = false;
        }
      });
      return hasValidControls;
    }
    return true;
  }

  onAddControl() {
    this.hazard.controls.push(new HazardControlModel());
  }

  onRemoveControl(i: number) {
    this.hazard.controls.splice(i, 1);
  }

  getRiskAssessmentColorChange(likelihood: number, severity: number) {
    const riskAssessment = this.utils.getRiskAssessmentText(
      likelihood,
      severity
    );

    if (['Low'].find((value) => value === riskAssessment)) {
      return 'success';
    }

    if (['Moderate'].find((value) => value === riskAssessment)) {
      return 'warning';
    }

    if (['High'].find((value) => value === riskAssessment)) {
      return 'warning-secondary';
    }

    if (['Extreme'].find((value) => value === riskAssessment)) {
      return 'danger';
    }

    return 'danger';
  }

  onRemove() {
    this.utils.showModal(
      'Archive Hazard/Risk',
      'Are you sure you want to archive this Hazard/Risk?',
      () => {
        this.oldApi.laravelApiRequest(
          'delete',
          'hazards/' + this.hazard.id + (this.hazard.site_id ? '?site_id=' + this.hazard.site_id : ''),
          {},
          {},
          () => {
            this.utils.showToast('The Hazard/Risk was archived.');
            this.dialogRef.close(true);
            this.inProgress = false;
          },
          (error) => {
            this.utils.showModal('Error', error.message);
          }
        );
      }
    );
  }

  onSelectReviewer() {
    this.utils.showComponentDialog(
      NetworkedUsersSelectorComponent,
      {
        multiple: false,
        selected: [this.hazard.reviewer_id],
        selectedAccountId: this.app.account.id,
        visitors_from_all_sites: true
      },
      {
        width: '1200px'
      },
      (userId: number) => {

        if ( typeof userId == 'undefined' ) {
          return;
        }

        this.hazard.reviewer_id = userId;
      }
    );

  }

  /**
   * Updates the view matrix based on the type parameter.
   *
   * @param {string} [type='Initial'] - The type of view matrix to update. Valid values are 'Initial' and 'Residual'.
   *
   * @return {void}
   */
  onViewMatrix(type: string = 'Initial') {
    type RiskLevelType = 1 | 2 | 3 | 4 | 5;
    let likelihood_selected: RiskLevelType = 2;
    let severity_selected: RiskLevelType = 2;

    switch (type) {
      case 'Initial':
        likelihood_selected = this.hazard.ira_likelihood;
        severity_selected = this.hazard.ira_severity;
        break;
      case 'Residual':
        likelihood_selected = this.hazard.rra_likelihood;
        severity_selected = this.hazard.rra_severity;
        break;
      default:
        return;
    }

    this.utils.showComponentDialog(
      HazardsRisksEditComponent,
      {
        type: type,
        selected: {
          likelihood: likelihood_selected,
          severity: severity_selected
        } as RiskLevel
      },
      {},
      (response: RiskLevel) => {

        if ( typeof response == 'undefined' ) {
          return;
        }

        if ([1, 2, 3, 4, 5].includes(response.likelihood) && [1, 2, 3, 4, 5].includes(response.severity)) {
          if(type == 'Initial') {
            this.hazard.ira_likelihood = response.likelihood as RiskLevelType;
            this.hazard.ira_severity = response.severity as RiskLevelType;
            this.hazard.ira_score = response.score;
            this.hazard.ira_score_band = response.band;
          } else if(type == 'Residual') {
            this.hazard.rra_likelihood = response.likelihood as RiskLevelType;
            this.hazard.rra_severity = response.severity as RiskLevelType;
            this.hazard.rra_score = response.score;
            this.hazard.rra_score_band = response.band;
          }
        }
      }
    );
  }


  /**
   * Opens a component dialog to display the hierarchy of controls related to hazards.
   *
   * @returns {void}
   */
  onViewHierarchyOfControl() {
    this.utils.showComponentDialog(
      HazardsHierarchyOfControlsComponent,
      {},
      {width: '1200px'},
      (results) => {
        // No action needed.
    });
  }

  /**
   * Retrieves the details of the output date and time based on the provided event.
   * @param {any} event - The event object containing the output date and time details.
   * @return {void}
   */
  getOutputDateTimeDetails(event: any) {
    this.reviewedAtDateTime = event;
  }

}
